import { Dispatch, UnknownAction } from "@reduxjs/toolkit"
import { IAxiosDefaultDataObject } from "../../config"
import BaseApp, { IBaseApp, IBaseAppDataDisplayerConfig, IBaseAppDynamicBasePage, IBaseAppFormConfig, IGridDataAdditional, ISearchParam } from "../BaseApp"
import LocationsApp from "../locations/LocationsApp"
import { addNewPriner, deletePrinter, setSelectedPrinters, updateAllPrinters, updatePrinersSearchParams, updatePrinter, updatePrintersLoadingStatus, updatePrintersPagination, updatePrintersSearch, useReturnAllPrinters, useReturnPrintersLoadingStatus, useReturnPrintersPagination, useReturnPrintersSearch, useReturnPrintersSearchParams, useReturnSelectedPrinters } from "./printersSlice"
import { GridDataColumns, IGridDataConfig } from "../../components/GridData"
import api from "../../services/api"
import SecretKey from "../../components/SecretKey"

/**
 * Printer Model
 */
export interface IPrinterModel {
    id: number,
    name: string,
    printer_key: string,
    description: string,
    printer_model: string,
    location_id: number,
    is_active: boolean,
    created_at: string,
    thumbnail: string,
    last_seen_at: string,
    is_online: boolean,
    status: string,
    location_title: string,
}

export const EmptyPrinterModel: IPrinterModel = {
    id: 0,
    name: "",
    printer_key: "",
    description: "",
    printer_model: "",
    location_id: 0,
    is_active: false,
    created_at: "",
    thumbnail: "",
    last_seen_at: "",
    is_online: false,
    status: "",
    location_title: "",
}

/**
 * Printer Job Model
 */
export interface IPrinterJobModel {
    id: number,
    printer_id: number,
    printer_key: string,
    label_type: string,
    payload: {},
    raw_ezpl: string,
    status: string,
    copies: number,
    created_by_id: number,
    created_at: string,
    claimed_at: string,
    printed_at: string,
    error: string,
}

export type PrintLabelRequest = {
  printer_key?: string;
  copies?: number;
  print_contained_items?: boolean;
  exclude_item_ids?: Number[];
}

export type PrintLabelResponse = {
  job_id: number;
  status: "pending" | "printing" | "printed" | "failed";
}

export type PrintKeyRefreshResponse = {
    message: string,
    printer_key: string,
}

/**
 * Printers App
 */
export default class PrintersApp extends BaseApp implements IBaseApp {

    appName = "printers"
    appBaseEndPoint = "/printing/printers"

    // customListEndPoint: string = this.appBaseEndPoint

    messages = {
        create: "Create a new printer",
        createShort: "New printer",
        edit: "Edit printer",
        editShort: "Edit",
        delete: "Delete printer",
        deleteShort: "Delete",
        view: "View printer",
        nothingFound: "No printers have been found.",
    }

    elemPrefix: string = "printer_"

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [
            {
                id: "name",
                type: "text",
                label: "Name"
            },
             {
                id: "status",
                type: "status",
                label: "Status"
            },
            {
                id: "location_title",
                type: "text",
                label: "Location"
            },
            {
                id: "printer_key",
                type: "text",
                label: "Printer Key",
                customContent: (item) => {
                    return <SecretKey itemId={item.id} secret_key={item.printer_key} hide={true} refreshToken={this.refreshPrinterKey} />
                },
            },
            {
                id: "description",
                type: "text",
                label: "Description"
            },
            {
                id: "last_seen_at",
                type: "date",
                label: "Last seen"
            }
        ],
        notionFound: {
            title: "Nothing...",
            message: "No printers have been found :("
        }
    }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "brands_form",
            elements: [
                {
                    id: this.elemPrefix + "name",
                    xs: 6,
                    type: "TextField",
                    label: "Title",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "printer_model",
                    xs: 6,
                    type: "Select",
                    label: "Printer Model",
                    fullWidth: true,
                    items: [
                        {
                            name: "GODEX_G300",
                            value: "GODEX_G300",
                        }
                    ]
                },
                {
                    id: this.elemPrefix + "description",
                    xs: 12,
                    type: "TextField",
                    label: "Description",
                    fullWidth: true,
                    multiline: true,
                    maxRows: 10,
                    minRows: 4,
                },
                {
                    id: "location_id",
                    xs: 12,
                    type: "DynamicModelSelect",
                    label: "Location",
                    fullWidth: true,
                    appClass: new LocationsApp(),
                    selectLabel: "Select Location",
                    gridColumns: new LocationsApp().gridDataColumns,
                    filterForm: new LocationsApp().gridConfig.filterForm
                },
                {
                    id: this.elemPrefix + "thumbnail",
                    xs: 12,
                    type: "ImageUploader",
                    label: "Thumbnail",
                    fullWidth: true
                },
            ]
        },
        sectionBox: {
            showTitle: true,
            title: "Details",
            description: "Fill in the printer details"
        },
        requiredFields: [
            {
                id: this.elemPrefix + "name",
                message: "Printer name is required!"
            },
            {
                id: this.elemPrefix + "location_id",
                message: "Printer location is required!"
            },
            {
                id: this.elemPrefix + "printer_model",
                message: "Printer model is required!"
            },
        ]
    }

    /**
     * Redux
     */
    redux = {
        updateItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => dispatch(updatePrinter((item as IPrinterModel))),
        addNewItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => dispatch(addNewPriner((item as IPrinterModel)))
    }

    /**
     * Dynamic Base Page
     */
    dynamicBasePage: IBaseAppDynamicBasePage = {
        items: useReturnAllPrinters,
        pagination: useReturnPrintersPagination,
        loading: useReturnPrintersLoadingStatus,
        search: useReturnPrintersSearch,
        updateLoadingStatus: (status: boolean, dispatch: Dispatch<UnknownAction>) => dispatch(updatePrintersLoadingStatus({ loading: status })),
        deleteItem: (id: number, dispatch: Dispatch<UnknownAction>) => dispatch(deletePrinter({ id: id })),
        addNewItem: (item, dispatch) => dispatch(addNewPriner((item as IPrinterModel))),
        updateItem: (item, dispatch) => dispatch(updatePrinter((item as IPrinterModel))),
        updateAllItems: (items, dispatch) => dispatch(updateAllPrinters((items as IPrinterModel[]))),
        updateSearch: (search: string, dispatch: Dispatch<UnknownAction>) => dispatch(updatePrintersSearch({ search: search })),
        updateItemsPagination: (pagination, dispatch) => dispatch(updatePrintersPagination(pagination)),
        itemsActionOptions: [
            {
                icon: "edit",
                label: "Edit",
                link: "/panel/printers/edit/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/edit/" + rawItem.id)
                },
            },
            {
                icon: "show",
                label: "View",
                link: "/panel/printers/view/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/view/" + rawItem.id)
                },
            },
        ],
        // filterForm: [],
        // customHeaderOptionActionButtons: [],
        // viewItemTemplateLink: "",
        // disableAutoViewItemLink: false,
        // hidePageHeadSection: false,
        // customTitlePageHeadSection: "",
        // disableSearch: false,
        // customPageHeadSectionActionButtons: []
    }

  /**
     * Grid data
     */
    gridDataColumns: GridDataColumns = [
        {
            field: "name",
            headerName: "Printer",
            type: "title",
            width: 350,
            minWidth: 350,
            cellRendererParams: {
                appClass: () => new PrintersApp(),
                enableQuickPreview: true,
            }
        },
        {
            field: "status",
            headerName: "Status",
            type: "status",
            width: 150,
        },
        {
            field: "printer_model",
            headerName: "Printer Model",
            type: "text",
            width: 250,
        },
        {
            field: "location_title",
            headerName: "Location",
            type: "quick-preview",
            width: 250,
            cellRendererParams: {
                appClass: () => new LocationsApp(),
                elemIdTitle: "location_id"
            }
        },
        {
            field: "created_at",
            headerName: "Date Created",
            type: "date",
            width: 250,
        },
    ]
    useGridData: boolean = true
    gridConfig: IGridDataConfig = {
        viewItemLink: "/panel/printers/view/[id]",
        editItemLink: "/panel/printers/edit/[id]",
    }
    gridDataEssentials: IGridDataAdditional  = {
        selectedItems: useReturnSelectedPrinters,
        searchParams: useReturnPrintersSearchParams,
        actionButtons: [],
        headerOptions: [],
        setSelectedItems: (items, dispatch) => dispatch(setSelectedPrinters(items)),
        updateItemSearchParams: (params, dispatch) => dispatch(updatePrinersSearchParams(params))
    }

    /**
     * Prin item label
     */
    printItemLabel = async(
        itemId: number,
        payload: PrintLabelRequest = {},
    ): Promise<PrintLabelResponse> => {
        return await api.post(`/printing/items/${itemId}/print-label/`, {
            printer_key: payload.printer_key ?? "warehouse_mac_1",
            copies: payload.copies ?? 1,
            print_contained_items: payload.print_contained_items,
            exclude_item_ids: payload.exclude_item_ids,
        })
    }

    /**
     * Online printers list
     */
    onlinePrintersList = async() => {

        let path: string = this.appBaseEndPoint + "/"

        if (this.customListEndPoint){
            path = this.customListEndPoint
        }

        path += "?disable_pagination=1&online_printers=1"

        return await api.get(path)

    }

    /**
     * Refresh printer key
     */
    refreshPrinterKey = async(
        printerId: number
    ) => {
        return await api.post(`/printing/printers/${printerId}/refresh-key`, {})
    }

    /**
     * Printer jobs list
     */
    printerJobsList = async(printerId: number, page: number, search: string | null, disablePagination: boolean, addItems?: boolean, searchParams?: ISearchParam[]) => {

        let path: string = this.appBaseEndPoint + "/" + printerId + "/jobs"

        if (this.customListEndPoint){
            path = this.customListEndPoint
        }

        path += `?page=${page ? page : 1}`

        if (search !== null){
            path += "&search=" + search
        }

        if (searchParams && searchParams.length > 0){
            searchParams.map((searchParam: ISearchParam) => {
                path += `&${searchParam.name}=${searchParam.value}`
            })
        }

        if (disablePagination){
            path += "&disable_pagination=1"
        }

        return await api.get(path)

    }

    /**
     * Delete instance
     */
    deletePrinterJob = async(id: number) => {
        return await api.delete(this.appBaseEndPoint + "/jobs" + id)
    }

    /**
     * Delete instance
     */
    deleteManyPrinterJobs = async(records: IAxiosDefaultDataObject, permanentlyDelete?: boolean) => {
        return await api.delete(this.appBaseEndPoint + "/jobs/delete-many", { data:{
            records: records,
            permanently_delete: permanentlyDelete,
        } })
    }

}